import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Send, Check, AlertCircle, Building2, User, Mail, Phone, 
  Globe, FileText, Calendar, ArrowLeft, Loader2 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const ORGANIZATION_TYPES = [
  { value: 'company', label: 'Company / Business' },
  { value: 'ngo', label: 'NGO / Non-Profit' },
  { value: 'sports_club', label: 'Sports Club / Team' },
  { value: 'federation', label: 'Sports Federation' },
  { value: 'school', label: 'School / University' },
  { value: 'government', label: 'Government Agency' },
  { value: 'individual', label: 'Individual / Freelancer' },
  { value: 'other', label: 'Other' }
];

const BecomeOrganizerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    organization_type: '',
    reason: '',
    experience: '',
    expected_events: '',
    website: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.name || !formData.email) {
      setError('Please fill in your name and email');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/api/organizers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit application');
      }
      
      setSubmitted(true);
      toast.success('Application submitted successfully!');
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <MegaMenu />
        <div className="max-w-2xl mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card-bg border border-border rounded-2xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Application Submitted!</h1>
            <p className="text-gray-400 mb-8">
              Thank you for your interest in becoming an event organizer on Michezonet.
              We'll review your application and get back to you within 2-3 business days.
            </p>
            <p className="text-sm text-gray-500 mb-8">
              We've sent a confirmation to <strong className="text-white">{formData.email}</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="border-border text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/events">
                <Button className="bg-green-600 hover:bg-green-700">
                  Browse Events
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="become-organizer-page">
      <MegaMenu />
      
      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-green-900/50 to-background">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Become an Event Organizer
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Join Michezonet and create amazing sports and entertainment events. 
              Get access to powerful tools for ticketing, registration, and attendee management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card-bg border border-border rounded-2xl p-6 md:p-8"
          >
            <h2 className="text-xl font-semibold text-white mb-6">Application Form</h2>
            
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="John Kamau"
                    data-testid="organizer-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="john@example.com"
                    data-testid="organizer-email-input"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="+254 7XX XXX XXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Website (Optional)
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
              
              {/* Organization Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Organization Name
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Your organization or company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Organization Type
                  </label>
                  <select
                    name="organization_type"
                    value={formData.organization_type}
                    onChange={handleChange}
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="organization-type-select"
                  >
                    <option value="">Select type...</option>
                    {ORGANIZATION_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Why do you want to organize events on Michezonet?
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Tell us about your goals and what you hope to achieve..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Previous Event Experience
                </label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Describe any events you've organized before..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  What types of events do you plan to create?
                </label>
                <textarea
                  name="expected_events"
                  value={formData.expected_events}
                  onChange={handleChange}
                  rows={2}
                  className="w-full bg-background border border-border rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Sports tournaments, fitness classes, corporate events..."
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-medium"
                data-testid="submit-application-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                By submitting, you agree to our{' '}
                <Link to="/terms" className="text-green-500 hover:text-green-400">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-green-500 hover:text-green-400">Privacy Policy</Link>
              </p>
            </form>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BecomeOrganizerPage;
